import { ComponentsTypes } from '@/constants/componentsTypes';
import { ToggleListItem as ToggleListItemType } from '@/schemas/sceneDataSchema';
import { Box, Icon, styled, Typography } from '@mui/material';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { DynamicFormFieldRenderer } from '@/components/DynamicUI/DynamicFormFieldRenderer';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import { TypographyColors } from '@/constants/typographyTypes';
import { ChipColors, ChipSizes, ChipVariant } from '@/constants/chipTypes';
import { InertChip } from '@/components/DynamicUI/InertChip';

interface ToggleListItemProps {
  component: ToggleListItemType;
  slotRight?: React.ReactNode;
  form?: UseFormReturn;
}

export const ToggleListItem: React.FC<ToggleListItemProps> = ({
  component: { icon, overline, label1, label2, supporting_text, with_dividers, toggle_component, trailing_pill },
  form,
}) => {
  const isInteractive = !!toggle_component && !!form;
  const fieldValue = isInteractive && (form.watch(encodeFieldKey(toggle_component)) as string);

  const supporting_textResult = useCallback(() => {
    if (!supporting_text) return null;

    if (supporting_text.static) {
      return supporting_text.static;
    }

    if (!toggle_component) {
      return supporting_text.on;
    }

    if (toggle_component.type == ComponentsTypes.CHECKBOX && !!fieldValue) {
      return supporting_text.on;
    }

    if (toggle_component.type == ComponentsTypes.SWITCH) {
      if (toggle_component.options[0].value == fieldValue) {
        return supporting_text.on;
      }
    }
    return supporting_text.off;
  }, [supporting_text, toggle_component, fieldValue]);

  return (
    <StyledListItem
      withDividers={!!with_dividers}
      isInteractive={isInteractive}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'text.primary' }}>
        {icon && <Icon>{icon}</Icon>}
        <TextGroup>
          {overline && (
            <Typography
              variant='overline'
              color={TypographyColors.TEXT_SECONDARY}
            >
              {overline}
            </Typography>
          )}
          <Typography variant='body1'>{label1}</Typography>
          {label2 && <Typography variant='body1'>{label2}</Typography>}
          {supporting_textResult && (
            <Typography
              variant='body2'
              color={TypographyColors.TEXT_SECONDARY}
            >
              {supporting_textResult()}
            </Typography>
          )}
        </TextGroup>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {trailing_pill && (
          <InertChip
            label={trailing_pill}
            size={ChipSizes.SMALL}
            color={ChipColors.TEXT}
            variant={ChipVariant.FILLED}
          />
        )}
        {toggle_component && (
          <DynamicFormFieldRenderer
            component={toggle_component}
            form={form}
          />
        )}
      </Box>
    </StyledListItem>
  );
};

const StyledListItem = styled('label')<{
  withDividers?: boolean;
  isInteractive?: boolean;
}>`
  cursor: ${({ isInteractive }) => isInteractive && 'pointer'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  padding-right: 4px;
  /* Handle optional dividers between ListItems */
  ${({ withDividers }) => withDividers && 'border-bottom: 1px solid var(--mui-palette-border-main);'}
  &:last-of-type {
    border-bottom: none;
  }
`;

const TextGroup = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  user-select: none;
  & > span,
  & > p {
    line-height: 1;
  }
`;
